import React, { useEffect, useRef, useState } from 'react';

import { Autocomplete, AutocompleteItem, AutocompleteProps } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import useAutoCompleteAppointmentType from 'hooks/appointmentType/useAutoCompleteAppointmentType';

import SelectorButton from './SelectorButton';

export interface AppointmentTypeAutoCompleteValue extends AutocompleteItem {
  appointmentTypeId: string;
  label: string;
}

interface IAppointmentTypeAutoCompleteProps
  extends Omit<
    AutocompleteProps,
    'data' | 'value' | 'onChange' | 'filter' | 'onItemSubmit' | 'rightSection'
  > {
  value: AppointmentTypeAutoCompleteValue;
  onChange: (item: AppointmentTypeAutoCompleteValue | null) => void;
  onClearValue?: () => void;
}

const AppointmentTypeAutoComplete = ({
  value,
  onChange,
  onClearValue,
  ...autocompleteProps
}: IAppointmentTypeAutoCompleteProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [options, setOptions] = useState<AutocompleteItem[]>([]);
  const [search, setSearch] = useState(value.label);
  const [debouncedSearch] = useDebouncedValue(search, 250);

  const { data, isSuccess, isLoading } = useAutoCompleteAppointmentType({
    search: debouncedSearch,
  });

  const handleSearchChange = (query: string): void => {
    setSearch(query);
    onChange(null);
  };
  const handleItemSubmit = (item: AppointmentTypeAutoCompleteValue): void => {
    setSearch('');
    onChange(item);
  };
  const handleClearSearch = (): void => {
    setSearch('');
  };

  const RightSection = (
    <SelectorButton
      isLoading={isLoading}
      hasText={!!(value.label || search)}
      handleClick={() => {
        handleSearchChange('');

        if (onClearValue) {
          onClearValue();
        }
      }}
      handleFocus={() => inputRef?.current?.focus()}
    />
  );

  useEffect(() => {
    if (isSuccess && data) {
      setOptions(
        data.data.map(appointmentType => ({
          ...appointmentType,
          value: appointmentType.appointmentTypeId,
          label: appointmentType.name,
        })),
      );
    }
  }, [isSuccess, data]);

  return (
    <Autocomplete
      ref={inputRef}
      rightSection={RightSection}
      value={value.label || search}
      onChange={handleSearchChange}
      onItemSubmit={handleItemSubmit}
      onDropdownClose={handleClearSearch}
      filter={() => true}
      data={options}
      limit={15}
      maxDropdownHeight={200}
      {...autocompleteProps}
    />
  );
};

export default AppointmentTypeAutoComplete;
